import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, UserX, ShieldAlert, Mail, CreditCard, Loader2, AlertCircle, MailCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Footer from '../components/Footer';

const API_URL =
  process.env.REACT_APP_ACCOUNT_DELETION_URL ||
  'https://eztfytmvkufcsimtbkmo.supabase.co/functions/v1/account-deletion-web';

// ─── Email form ──────────────────────────────────────────────────────────────
const EmailForm = ({ onSuccess }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || 'Something went wrong. Please try again.');
      }

      onSuccess();
    } catch (err) {
      console.error('Account deletion request error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border-2 border-[#FF7F30]/20 rounded-2xl p-8 space-y-6">
      <h2 className="text-2xl font-bold text-[#333333] text-center">
        Request account deletion
      </h2>
      <p className="text-[#666666] text-center max-w-xl mx-auto">
        Enter the email address associated with your LinguPix account.
        We will send you a confirmation link with the next steps.
      </p>

      <form onSubmit={handleSubmit} className="max-w-md mx-auto space-y-4">
        {error && (
          <div className="bg-[#FF4444]/10 border border-[#FF4444] rounded-xl p-4 flex items-start space-x-3">
            <AlertCircle size={20} className="text-[#FF4444] flex-shrink-0 mt-0.5" />
            <p className="text-[#FF4444] text-sm">{error}</p>
          </div>
        )}

        <div className="space-y-2">
          <label htmlFor="deletion-email" className="text-sm font-semibold text-[#333333]">
            Email address
          </label>
          <Input
            id="deletion-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email address"
            className="h-12 border-gray-300 focus:border-[#FF7F30] focus:ring-[#FF7F30]"
            required
            disabled={loading}
          />
        </div>

        <Button
          type="submit"
          disabled={loading || !email}
          className="w-full bg-[#FF7F30] hover:bg-[#FFA55A] text-white font-semibold h-12 text-lg"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 animate-spin" size={20} />
              Sending...
            </>
          ) : (
            'Send confirmation link'
          )}
        </Button>
      </form>
    </div>
  );
};

// ─── "Check your email" state ────────────────────────────────────────────────
const EmailSent = () => (
  <div className="bg-white border-2 border-[#4CAF50]/20 rounded-2xl p-8 text-center space-y-6">
    <div className="w-20 h-20 bg-[#4CAF50]/10 rounded-full flex items-center justify-center mx-auto">
      <MailCheck size={40} className="text-[#4CAF50]" />
    </div>
    <h2 className="text-2xl font-bold text-[#333333]">
      Check your email
    </h2>
    <div className="space-y-3 max-w-lg mx-auto">
      <p className="text-[#666666] leading-relaxed">
        If the email address belongs to a LinguPix account, we have sent
        a confirmation link with the next steps.
      </p>
      <p className="text-[#999999] text-sm leading-relaxed">
        For security reasons, this page shows the same response whether
        the account exists or not.
      </p>
    </div>
  </div>
);

// ─── Main page component ────────────────────────────────────────────────────
const DeleteAccount = () => {
  const [emailSent, setEmailSent] = useState(false);

  return (
    <div className="min-h-screen bg-white flex flex-col">
        <link rel="canonical" href="https://www.lingu-pix.app/delete-account" />
        <title>Delete Account – LinguPix</title>
        <meta
          name="description"
          content="Request deletion of your LinguPix account and all associated personal data."
        />
        <meta name="robots" content="noindex, nofollow" />

      {/* Header */}
      <header className="bg-[#333333] text-white py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center text-[#FFA55A] hover:text-white transition-colors mb-4"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Home
          </Link>
          <h1 className="text-4xl font-bold">Delete Your LinguPix Account</h1>
          <p className="text-gray-300 mt-2">
            Account &amp; data deletion request
          </p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex-1">
        {/* Intro */}
        <div className="bg-[#FFF3E0] border border-[#FFE5CC] rounded-2xl p-8 mb-10 flex items-start gap-5">
          <div className="hidden sm:flex w-14 h-14 rounded-full bg-[#FF7F30]/10 items-center justify-center flex-shrink-0 mt-1">
            <UserX size={28} className="text-[#FF7F30]" />
          </div>
          <div className="space-y-4">
            <p className="text-[#333333] text-lg leading-relaxed">
              You can request deletion of your LinguPix account and related
              personal data using our secure deletion page.
            </p>
            <p className="text-[#333333] text-lg leading-relaxed">
              For security reasons, we will first send a confirmation link
              to the email address associated with your account.
            </p>
          </div>
        </div>

        {/* Process overview */}
        <div className="space-y-8 mb-12">
          <h2 className="text-2xl font-bold text-[#333333]">
            What happens after confirmation
          </h2>

          <div className="grid sm:grid-cols-3 gap-6">
            <div className="bg-[#F5F5F5] rounded-2xl p-6 space-y-3">
              <div className="w-10 h-10 rounded-full bg-[#FF7F30]/10 flex items-center justify-center">
                <ShieldAlert size={20} className="text-[#FF7F30]" />
              </div>
              <h3 className="font-semibold text-[#333333]">
                30-day grace period
              </h3>
              <p className="text-[#666666] text-sm leading-relaxed">
                After confirmation, your account is blocked and enters a 30-day
                grace period. During this time, you can contact us to request
                cancellation of the deletion.
              </p>
            </div>

            <div className="bg-[#F5F5F5] rounded-2xl p-6 space-y-3">
              <div className="w-10 h-10 rounded-full bg-[#FF7F30]/10 flex items-center justify-center">
                <Mail size={20} className="text-[#FF7F30]" />
              </div>
              <h3 className="font-semibold text-[#333333]">
                Email notifications
              </h3>
              <p className="text-[#666666] text-sm leading-relaxed">
                You will receive two follow-up emails after confirmation: one
                confirming that deletion has been scheduled, and another
                confirming that your data has been permanently removed after
                the grace period ends.
              </p>
            </div>

            <div className="bg-[#F5F5F5] rounded-2xl p-6 space-y-3">
              <div className="w-10 h-10 rounded-full bg-[#FF7F30]/10 flex items-center justify-center">
                <CreditCard size={20} className="text-[#FF7F30]" />
              </div>
              <h3 className="font-semibold text-[#333333]">
                Subscriptions
              </h3>
              <p className="text-[#666666] text-sm leading-relaxed">
                Deleting your account does not cancel your subscription.
                If you purchased through the App Store or Google Play,
                billing and cancellation are managed directly in the store.
              </p>
            </div>
          </div>
        </div>

        {/* Form or "Check your email" */}
        {emailSent ? (
          <EmailSent />
        ) : (
          <EmailForm onSuccess={() => setEmailSent(true)} />
        )}
      </main>

      <Footer />
    </div>
  );
};

export default DeleteAccount;
